import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from '@tanstack/react-virtual';
import { SessionDetailView, viewRequiresHeader, viewCanSeeComments, viewSupportsTexting } from "components/History/views";
import { SessionHistoryProps } from "./cfg";
import { SessionListResponse } from "interfaces/sessions";
import { SessionV2Columns } from "interfaces/table";
import { ConversationType, Platform } from "interfaces/db";
import { LoadingState } from "components/SessionDetails/Defaults/Loading";
import { NoDataState } from "components/SessionDetails/Defaults/NoDataState";
import { DateDivider } from "./DateDivider";
import { TranscriptPreview } from "./TranscriptPreview";
import { TextPreview } from "./TextPreview";
import { SessionHistoryHeader } from "./SessionHistoryHeader";
import { PROSPECT_INFO_COLUMNS } from "interfaces/table";
import { SessionDetails } from "components/SessionDetails";
import { History } from 'lucide-react';
import { Maximize2 } from 'lucide-react';
import { CommentPanel } from "../SessionDetails/TranscriptView/CommentPanel";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { TextComposer } from "./TextComposer";
import { PendingText } from "./PendingText";
import { PendingSend } from "lib/redux/sessions/texting/slice";
import { useSessionsByNumbers, SessionByNumbersItem } from "lib/redux/sessions/lists/session-by-numbers-hook";
import { useSessionDetails } from "lib/redux/sessions/data/data-hook";
import { useSessionListCustomScores } from "../../lib/redux/sessions/custom-scores/session-list-custom-scores-hook";
import { useSessionMemoryManager } from "../../hooks/useSessionMemoryManager";

// Styles for the conversation history component
const styles = {
  conversationHistoryContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    backgroundColor: '#f5f5f5',
    position: 'relative' as const,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto' as const,
  },
  conversationItems: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  conversationItem: (isEmbedded: boolean, isTextItem: boolean = false) => ({
    padding: isTextItem ? 0 : (isEmbedded ? '8px 12px' : '12px 16px'),
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: '8px',
    fontSize: isEmbedded ? '12px' : '14px',
    position: 'relative' as const,
    border: '2px solid transparent',
  }),
  selectedItem: {
    boxShadow: '0 0 0 2px #4F46E5, 0 1px 3px rgba(0,0,0,0.1)',
    backgroundColor: '#f0f8ff',
  },
  virtualItem: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    paddingTop: '3px',
    paddingBottom: '3px',
  },
  fullScreenOverlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'white',
    zIndex: 50,
  },
  historyBanner: (isEmbedded: boolean) => ({
    backgroundColor: '#f0f9ff',
    borderBottom: '1px solid #e5e7eb',
    padding: isEmbedded ? '8px 12px' : '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: isEmbedded ? '8px' : '12px',
    cursor: 'pointer',
    transition: 'all 0.15s ease-in-out',
    flexShrink: 0,
  }),
};

// Define types for the flattened items
type HeaderItem = {
  type: 'header';
  date: string;
};

type SessionItem = {
  type: 'item';
  data: SessionListResponse;
  index: number;
};

type PendingTextItem = {
  type: 'pendingText';
  data: PendingSend;
  index: number;
};

type FlattenedItem = HeaderItem | SessionItem | PendingTextItem;

// Add new state type for view mode
type ViewMode = 'list' | 'single';

// Type for text thread pairs
interface TextThreadPair {
  repNumber: string;
  prospectNumber: string;
  label: string;
  count: number;
}

export function ConversationHistory(props: SessionHistoryProps) {
  // Move all hooks to the top level
  const { view, onClose, sessionId, phone_numbers, tab } = props;
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedSessionId, setSelectedSessionId] = useState<string | undefined>(sessionId);
  const [isCommentPanelOpen, setIsCommentPanelOpen] = useState(false);
  const [activeTextThreadPair, setActiveTextThreadPair] = useState<TextThreadPair | null>(null);
  const scrolledForSessionIds = useRef<Set<string>>(new Set())

  // Simplified stable phone numbers - avoid sort() which is expensive
  const phoneNumbersKey = useMemo(() => phone_numbers.join(','), [phone_numbers]);
  const stablePhoneNumbers = useMemo(() => phone_numbers, [phone_numbers]);

  useEffect(() => {
    scrolledForSessionIds.current.clear();
    setSelectedSessionId(sessionId)
  }, [sessionId])

  useEffect(() => {
    scrolledForSessionIds.current.clear();
    setActiveTextThreadPair(null);
  }, [phoneNumbersKey]) 
  
  const parentRef = useRef<HTMLDivElement>(null);
  const can_text = useSelector((state: RootState) => state.team.can_text)

  // Use the consolidated hook - all texting and session data comes from here now
  const { 
    items, // This is now the sorted combined data
    sessionItems, // Raw session data for transcript count
    isLoading, 
    isDebouncing,
    hasMore,
    loadMore,
    refresh,
    successfulTexts,
    incomingTexts,
  } = useSessionsByNumbers({
    numbers: stablePhoneNumbers,
    view,
    selectedSessionId
  });

  // Only fetch session details for the selected session - MOVED FROM TranscriptPreview
  useSessionDetails({sessionId: selectedSessionId })

  // Get custom scores for all sessions
  const { getCustomScoresForSession } = useSessionListCustomScores({ sessions: sessionItems, view });

  // Extract session IDs for memory management
  const currentSessionIds = useMemo(() => {
    const ids = sessionItems.map(item => item.session_id);
    // Also include the selected session ID if it exists
    if (selectedSessionId && !ids.includes(selectedSessionId)) {
      ids.push(selectedSessionId);
    }
    return ids;
  }, [sessionItems, selectedSessionId]);

  // Use memory manager to clean up old sessions
  useSessionMemoryManager({
    currentSessionIds,
    maxSessions: 15, // Keep up to 15 sessions in memory
    cleanupInterval: 30000 // Clean up every 30 seconds
  });

  // Simplified text tracking - avoid JSON.stringify which is expensive
  const previousSuccessfulTextsCountRef = useRef<number>(0);
  const currentSuccessfulTextsCount = successfulTexts.length;
  
  // Track incoming texts separately
  const previousIncomingTextsCountRef = useRef<number>(0);
  const currentIncomingTextsCount = incomingTexts.length;
  
  // Track total items count for scroll-to-bottom on new incoming messages
  const previousItemsCountRef = useRef<number>(0);
  const currentItemsCount = items.length;
  
  // Track scroll trigger for state-based scroll to bottom
  const [scrollTrigger, setScrollTrigger] = useState<number>(0);
  
  // Check if we should scroll when items change
  useEffect(() => {
    if (currentSuccessfulTextsCount > previousSuccessfulTextsCountRef.current) {
      setScrollTrigger(prev => prev + 1);
    }
    previousSuccessfulTextsCountRef.current = currentSuccessfulTextsCount;
  }, [currentSuccessfulTextsCount]);

  // Check if we should scroll when new incoming texts arrive
  useEffect(() => {
    if (currentIncomingTextsCount > previousIncomingTextsCountRef.current) {
      setScrollTrigger(prev => prev + 1);
    }
    previousIncomingTextsCountRef.current = currentIncomingTextsCount;
  }, [currentIncomingTextsCount]);

  // Check if we should scroll when new items are added (including incoming texts)
  useEffect(() => {
    if (currentItemsCount > previousItemsCountRef.current && currentItemsCount > 0 && !selectedSessionId) {
      setScrollTrigger(prev => prev + 1);
    }
    previousItemsCountRef.current = currentItemsCount;
  }, [currentItemsCount, selectedSessionId]);

  // Simplified active thread key - avoid expensive cleanNumber calls
  const activeThreadKey = useMemo(() => {
    if (!activeTextThreadPair) return null;
    return `${activeTextThreadPair.repNumber}-${activeTextThreadPair.prospectNumber}`;
  }, [activeTextThreadPair]);

  // Simplified filtering - avoid expensive operations when not needed
  const filteredItems = useMemo((): SessionByNumbersItem[] => {
    if (!activeThreadKey) return items;
    
    return items.filter((item: SessionByNumbersItem) => {
      if (item.type === 'pendingText') {
        const pendingData = item.data as PendingSend;
        const key = `${pendingData.sent_number.number}-${pendingData.target_phone_number}`;
        return key === activeThreadKey
      } else if (item.type === 'item') {
        const sessionData = item.data as SessionListResponse;
        const repNumber = sessionData.result.get(SessionV2Columns.AGENT_ADDRESS) as string;
        const prospectNumber = sessionData.result.get(SessionV2Columns.CUSTOMER_ADDRESS) as string;
        if (!repNumber || !prospectNumber) return false;
        const key = `${repNumber}-${prospectNumber}`;
        return key === activeThreadKey;
      }
      return false;
    });
  }, [items, activeThreadKey]);

  // Properly group by date while maintaining performance
  const flattenedItems = useMemo(() => {
    if (filteredItems.length === 0) return [];
    
    // Group items by date
    const dateGroups = new Map<string, typeof filteredItems>();
    
    filteredItems.forEach(item => {
      if (!item.start) return;
      const dateString = item.start.toDateString();
      
      if (!dateGroups.has(dateString)) {
        dateGroups.set(dateString, []);
      }
      dateGroups.get(dateString)!.push(item);
    });
    
    // Convert to sorted array (OLDEST dates first for chronological order)
    const sortedDateGroups = Array.from(dateGroups.entries())
      .sort((a, b) => {
        const dateA = new Date(a[0]);
        const dateB = new Date(b[0]);
        return dateA.getTime() - dateB.getTime(); // OLDEST dates first (chronological)
      });
    
    // Flatten with headers (NO load more item here anymore)
    const result: FlattenedItem[] = [];
    
    sortedDateGroups.forEach(([dateString, items]) => {
      // Add date header
      result.push({ type: 'header', date: dateString });
      
      // Sort items within the date group (OLDEST first within the day)
      const sortedItems = items.sort((a, b) => {
        if (!a.start || !b.start) return 0;
        return a.start.getTime() - b.start.getTime(); // OLDEST first within day
      });
      
      // Add items with proper typing
      sortedItems.forEach((item, i) => {
        if (item.type === 'pendingText') {
          result.push({ 
            type: 'pendingText',
            data: item.data as PendingSend,
            index: i
          });
        } else {
          result.push({ 
            type: 'item',
            data: item.data as SessionListResponse,
            index: i
          });
        }
      });
    });
    
    return result;
  }, [filteredItems]); // Removed hasMore and activeTextThreadPair since load more is no longer here

  // Simplified virtualization callbacks - avoid dependencies on full arrays
  const estimateSize = useCallback((index: number) => {
    const item = flattenedItems[index];
    if (!item) return 160;
    if (item.type === 'header') return 40;
    return 160; // Fixed size for items (removed loadMore case)
  }, [flattenedItems]);
  
  const getItemKey = useCallback((index: number) => {
    const item = flattenedItems[index];
    if (!item) return `item-${index}`;
    if (item.type === 'header') return `header-${item.date}`;
    if (item.type === 'pendingText') return `pending-${item.data.uuid}`;
    return `item-${item.data.session_id || index}`; // Removed loadMore case
  }, [flattenedItems]);

  const rowVirtualizer = useVirtualizer({
    count: flattenedItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize,
    overscan: 3, // Reduced overscan for better performance
    measureElement: undefined, // Disable automatic measuring to prevent ResizeObserver loops
    getItemKey,
  });

  // Simplified scroll to session - avoid expensive operations
  const scrollToSession = useCallback((targetSessionId: string) => {
    if (scrolledForSessionIds.current.has(targetSessionId) || activeTextThreadPair) return;
    
    const itemIndex = flattenedItems.findIndex(
      item => item.type === 'item' && item.data.session_id === targetSessionId
    );

    if (itemIndex !== -1) {
      scrolledForSessionIds.current.add(targetSessionId);
      setTimeout(() => {
        try {
          rowVirtualizer.scrollToIndex(itemIndex, { align: 'auto' });
        } catch (error) {
          // Silently handle scroll errors
        }
      }, 100); // Reduced timeout for faster response
    }
  }, [flattenedItems, rowVirtualizer, activeTextThreadPair]);

  // Handle scroll to bottom when needed
  useEffect(() => {
    if (scrollTrigger > 0 && rowVirtualizer && flattenedItems.length > 0) {
      try {
        // Use a small delay to ensure the virtualizer has updated with new items
        setTimeout(() => {
          try {
            rowVirtualizer.scrollToIndex(flattenedItems.length - 1, { align: 'end' });
          } catch (error) {
            // Silently handle scroll errors
          }
        }, 100);
      } catch (error) {
        // Silently handle scroll errors
      }
    }
  }, [scrollTrigger, rowVirtualizer, flattenedItems.length]);

  // Scroll to selected session
  useEffect(() => {
    if (selectedSessionId && sessionItems.length > 0) {
      scrollToSession(selectedSessionId);
    }
  }, [selectedSessionId, sessionItems.length, scrollToSession]);

  const handleExpand = useCallback((session_id: string) => {
    setViewMode('single');
  }, []);

  // Add toggleCommentPanel function
  const toggleCommentPanel = useCallback(() => {
    setIsCommentPanelOpen(!isCommentPanelOpen);
  }, [isCommentPanelOpen]);

  // Handle thread selection from TextComposer
  const handleThreadSelection = useCallback((thread: TextThreadPair | null) => {
    setActiveTextThreadPair(thread);
  }, []);

  // Simplified header data calculation - avoid expensive operations
  const headerData = useMemo(() => {
    // Default values
    const defaults = {
      name: 'Prospect',
      count: sessionItems.length,
      company_name: undefined,
      platform: undefined as Platform | undefined,
      platform_url: undefined,
      jobTitle: undefined
    };

    // Find the relevant session (selected or first)
    const relevantSession = sessionId 
      ? sessionItems.find(item => item.session_id === sessionId)
      : sessionItems[0];

    if (!relevantSession) return defaults;

    // Extract basic info quickly - avoid multiple .get() calls
    const result = relevantSession.result;
    const name = result.get(PROSPECT_INFO_COLUMNS.PROSPECT_NAME);
    const companyName = result.get(PROSPECT_INFO_COLUMNS.COMPANY_NAME);

    return {
      ...defaults,
      name: name ? String(name) : 'Caller',
      company_name: companyName ? String(companyName) : undefined,
    };
  }, [sessionItems, sessionId]); // Simplified dependencies

  // Show single session view when in 'single' mode
  if (viewMode === 'single' && selectedSessionId) {
    return (
      <div className="flex flex-col h-full">
        {/* History Banner */}
        <div 
          style={styles.historyBanner(view === SessionDetailView.COACHING)}
          onClick={() => setViewMode('list')}
          className={`hover:bg-blue-50 group ${view === SessionDetailView.COACHING ? 'text-sm' : 'text-base'}`}
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <History className={`${view === SessionDetailView.COACHING ? 'w-4 h-4' : 'w-5 h-5'} text-blue-600`} />
              <span className={`font-medium text-gray-900 ${view === SessionDetailView.COACHING ? 'text-xs' : 'text-sm'}`}>
                Back to Conversation History
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-gray-500 ${view === SessionDetailView.COACHING ? 'text-xs' : 'text-sm'}`}>
                {sessionItems.length} {sessionItems.length === 1 ? 'conversation' : 'conversations'}
              </span>
              <Maximize2 className={`${view === SessionDetailView.COACHING ? 'w-3 h-3' : 'w-4 h-4'} text-gray-400 group-hover:text-gray-600 transition-colors rotate-180`} />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <SessionDetails 
            sessionId={selectedSessionId}
            view={view}
            onClose={() => setViewMode('list')}
          />
        </div>
      </div>
    );
  }

  if (sessionItems.length === 0 && isLoading) return <LoadingState />;
  if (sessionItems.length === 0 && !isLoading && view !== SessionDetailView.TEXTING) return <NoDataState onRetry={refresh} />;

  // Otherwise show the conversation list view
  return (
    <div style={styles.conversationHistoryContainer}>
      {/* Sticky header */}
      {viewRequiresHeader(view) && <SessionHistoryHeader
        name={headerData.name}
        count={headerData.count}
        view={view}
        onClose={onClose}
        sessionId={sessionId}
        phone_numbers={stablePhoneNumbers} // Use stable phone numbers
        company_name={headerData.company_name}
        platform={headerData.platform}
        platform_url={headerData.platform_url}
        jobTitle={headerData.jobTitle}
        sessions={sessionItems}
      />}

      {/* Load More Banner at the top - only show if hasMore and not filtering by thread and not initial loading */}
      {hasMore && !activeTextThreadPair && sessionItems.length > 0 && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center justify-between min-h-[48px]">
          <div className="flex items-center space-x-3">
            <div className="text-sm text-blue-700">
              <span className="font-medium">{sessionItems.length} conversations loaded</span>
              <span className="text-blue-600 ml-2">• More available</span>
            </div>
          </div>
          <div className="flex items-center min-h-[32px]">
            {(isLoading || isDebouncing) ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
                <span className="text-sm text-blue-700">
                  {isDebouncing ? "Updating..." : "Loading..."}
                </span>
              </div>
            ) : (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  loadMore();
                }}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 h-8"
                disabled={isLoading || isDebouncing}
              >
                Load More
              </button>
            )}
          </div>
        </div>
      )}

      {/* Virtualized content */}
      <div ref={parentRef} style={{...styles.scrollableContent}}>
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
            paddingLeft: '16px',
            paddingRight: '16px',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const item = flattenedItems[virtualRow.index];
            if (!item) return null;
            
            // Simplified type checking - avoid expensive operations in render
            const isTextItem = item.type === 'pendingText';
            const isCallItem = item.type === 'item' && !isTextItem;
            
            return (
              <div
                key={virtualRow.key}
                ref={(el) => {
                  if (el) {
                    rowVirtualizer.measureElement(el);
                  }
                }}
                data-index={virtualRow.index}
                style={{
                  ...styles.virtualItem,
                  transform: `translateY(${virtualRow.start}px)`,
                  ...(item.type !== 'header' && !isTextItem && {
                    paddingLeft: '16px',
                    paddingRight: '16px',
                  }),
                  ...(isTextItem && {
                    paddingTop: 0,
                    paddingBottom: 0
                  })
                }}
              >
                {item.type === 'header' ? (
                  <DateDivider date={item.date} />
                ) : item.type === 'pendingText' ? (
                  <PendingText text={item.data as PendingSend} />
                ) : (
                  // Simplified rendering - assume item.data has the right structure
                  isCallItem && (item.data as SessionListResponse).conversation_type === ConversationType.CALL ? (
                    <TranscriptPreview
                      view={view} 
                      session={item.data as SessionListResponse} 
                      isSelected={item.data.session_id === selectedSessionId}
                      onSelect={() => setSelectedSessionId(item.data.session_id || undefined)}
                      onUnselect={() => setSelectedSessionId(undefined)}
                      onExpand={() => handleExpand(item.data.session_id || '')}
                      customScores={getCustomScoresForSession(item.data.session_id)}
                    />
                  ) : (
                    <TextPreview 
                      session={item.data as SessionListResponse} 
                      isSelected={item.data.session_id === selectedSessionId}
                    />
                  )
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Comment Panel - Attached to the right when a session is selected */}
      {viewCanSeeComments(view) && selectedSessionId && viewMode === 'list' && (
        <div className="w-64 border-l border-gray-200 bg-white overflow-y-auto">
          <CommentPanel
            sessionId={selectedSessionId}
            isOpen={isCommentPanelOpen}
            onToggle={toggleCommentPanel}
            onScrollToTranscript={(idx) => {}}
          />
        </div>
      )}

      {viewMode === 'list' && viewSupportsTexting(view) && can_text && stablePhoneNumbers.length > 0 && (
        <div className="sticky bottom-0 left-0 right-0">
          <TextComposer
            items={sessionItems}
            isLoading={isLoading}
            phoneNumbers={stablePhoneNumbers} // Use stable phone numbers
            onThreadSelected={handleThreadSelection}
            activeTextThreadPair={activeTextThreadPair}
          />
        </div>
      )}
    </div>
  );
}